/**
 * Hook personalizado para edição de recursos.
 *
 * Este hook gerencia o estado e a lógica necessários para editar um recurso existente,
 * incluindo carregamento de dados, validação de formulário e comunicação com a API.
 *
 * @module useEditResource
 */

import {
  ContentState,
  convertFromRaw,
  convertToRaw,
  EditorState,
} from 'draft-js';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../services/api';

/**
 * Hook que gerencia o fluxo de edição de um recurso.
 *
 * @returns {Object} Objeto contendo estados e funções para edição de recursos
 * @returns {EditorState} returns.editorContent - Estado do editor de texto
 * @returns {Function} returns.setEditorContent - Função para atualizar o estado do editor
 * @returns {Array} returns.categories - Lista de categorias disponíveis
 * @returns {string|number} returns.category - ID da categoria selecionada
 * @returns {Function} returns.setCategory - Função para atualizar a categoria
 * @returns {string} returns.categoryName - Nome da categoria selecionada
 * @returns {string} returns.type - Tipo do recurso
 * @returns {Function} returns.setType - Função para atualizar o tipo
 * @returns {string} returns.name - Nome/título do recurso
 * @returns {Function} returns.setName - Função para atualizar o nome
 * @returns {string} returns.errorMsg - Mensagem de erro
 * @returns {string} returns.successMsg - Mensagem de sucesso
 * @returns {Object} returns.errors - Objeto com erros de validação
 * @returns {boolean} returns.loading - Estado de carregamento
 * @returns {Function} returns.editResource - Função para submeter a edição do recurso
 */
const useEditResource = () => {
  // Hooks para tradução, navegação e obtenção de parâmetros
  const { t } = useTranslation();
  const location = useLocation();
  const { id } = location.state;
  const navigate = useNavigate();

  // Estados para gerenciar o formulário e o processo de edição
  const [editorContent, setEditorContent] = useState(() =>
    EditorState.createEmpty()
  );
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('');
  const [categoryName, setCategoryName] = useState('');
  const [type, setType] = useState('');
  const [name, setName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  // Busca categorias ao montar o componente
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const { data } = await api.get('categories');
        setCategories(data.resources);
      } catch (err) {
        console.error('Erro na requisição', err);
      }
    };

    fetchCategories();
  }, []);

  // Busca o recurso após as categorias estarem carregadas
  useEffect(() => {
    const fetchResource = async () => {
      try {
        const { data } = await api.get(`resource/${id}`);
        const resourceData = data.resources[0];

        setCategory(resourceData.category_id);
        setType(resourceData.type);
        setName(resourceData.title);

        const matched = categories.find(
          (cat) => parseInt(cat.id) === resourceData.category_id
        );
        setCategoryName(matched?.name || '');

        initializeEditorContent(resourceData.description);
      } catch (err) {
        console.error('Erro na requisição', err);
      } finally {
        setLoading(false);
      }
    };

    if (categories.length > 0 && id) {
      fetchResource();
    }
  }, [categories, id]);

  const cleanJsonString = (str) => str.replace(/\r?\n/g, '\\n');

  /**
   * Inicializa o editor de conteúdo com os dados do recurso.
   * Tenta converter o conteúdo de JSON para o formato do editor,
   * com fallback para texto simples em caso de erro.
   *
   * @function initializeEditorContent
   * @param {string|Object} description - Descrição do recurso em formato JSON ou string
   */
  const initializeEditorContent = (description) => {
    try {
      let desc = description;
      if (typeof desc === 'string') {
        desc = cleanJsonString(desc);
        desc = JSON.parse(desc);
      }
      const contentState = convertFromRaw(desc);
      setEditorContent(EditorState.createWithContent(contentState));
    } catch (error) {
      const fallbackState = ContentState.createFromText(description || '');
      setEditorContent(EditorState.createWithContent(fallbackState));
      console.error('Erro ao inicializar editor:', error);
    }
  };

  /**
   * Verifica se o conteúdo do editor está vazio.
   *
   * @function isEditorContentEmpty
   * @returns {boolean} True se o editor estiver vazio, false caso contrário
   */
  const isEditorContentEmpty = () => {
    const contentState = editorContent.getCurrentContent();
    return !contentState || contentState.getPlainText().trim() === '';
  };

  /**
   * Valida os campos do formulário antes de submeter.
   *
   * @function validateFormInputs
   * @returns {boolean} True se todos os campos forem válidos, false caso contrário
   */
  const validateFormInputs = () => {
    const newErrors = {};
    if (!category) newErrors.category = true;
    if (!type.trim()) newErrors.type = true;
    if (!name.trim()) newErrors.name = true;
    if (isEditorContentEmpty()) newErrors.editor = true;

    if (Object.keys(newErrors).length) {
      setErrors(newErrors);
      setErrorMsg(t('Please fill in all required fields.'));
      return false;
    }

    setErrors({});
    return true;
  };

  /**
   * Submete o formulário para atualizar o recurso na API.
   *
   * @async
   * @function editResource
   * @param {React.FormEvent} e - Evento de submit do formulário
   */
  const editResource = async (e) => {
    e.preventDefault();
    setErrorMsg('');

    if (!validateFormInputs()) return;

    const payload = {
      id_category: parseInt(category),
      type,
      title: name,
      description: JSON.stringify(
        convertToRaw(editorContent.getCurrentContent())
      ),
    };

    try {
      await api.put(`resources/${id}`, payload);

      setSuccessMsg('Recurso atualizado com sucesso!');
      setTimeout(() => navigate('/resources'), 2000);
    } catch (err) {
      setErrorMsg(t('Error updating resource'));
      console.error('Erro:', err);
    }
  };

  return {
    editorContent,
    setEditorContent,
    categories,
    category,
    setCategory,
    categoryName,
    type,
    setType,
    name,
    setName,
    errorMsg,
    successMsg,
    errors,
    loading,
    editResource,
  };
};

export default useEditResource;
